import { SsMount } from 'src/js/dom/mount.js';
import { SsCollection } from 'src/js/dom/collection.js';
import { SsConsole } from 'src/js/dom/console.js';
import { velocity } from 'src/js/lib/velocity.js';
import { throttle } from 'lodash';
import {
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import { resetHeadHash } from 'src/js/lib/url.js';
import { isLarge } from 'src/js/lib/snif.js';
import { doncyou } from 'src/js/dom/doncyou.js';

export class SsGallery extends SsMount {
  constructor ( elm ) {
    super( elm );
    this.cols = {};
    this.col = null;
    this.opend = false;

    // Controlled instances
    this.console = new SsConsole(
      document.getElementById( 'ss-gallery-console' )
    );

    // Event ID
    this.resizeID = throttle( this.resizeLsn.bind( this ), 60 );
    this.scrollID = throttle( this.scrollLsn.bind( this ), 300 );

    // velocity options
    this.vdefs = () => {
      return {
        axis: 'x',
        container: this.elm,
        offset: -this.elm.scrollLeft,
        duration: 280,
        easing: 'easeOutSine',
      };
    };

    // Attach event listenner
    this.setTouchEvent();
    this.setBtnEvent();
  }

  async load ( id, path, num ) {
    // Load a collection first time
    if ( typeof this.cols[ id ] === 'undefined' )
    {
      // Register a collection
      const col = this.cols[ id ] = new SsCollection( path, num );
      // Load all images in background
      await col.load();
    }
    // Set the collection
    this.col = this.cols[ id ];
    // Configure the console
    this.console.deno = num;
    return Promise.resolve();
  }

  async open ( index ) {
    // Short circuit
    if ( this.opend ) {
      return Promise.resolve();
    }
    // Toggle state
    this.opend = ! this.opend;
    // Emit event for sub page
    this.emit( 'open' );
    // Configure the console
    this.console.nume = index + 1;
    // Append the collection dom
    this.$.append( this.col.makeDom() );
    // Set a current item
    this.col.head = index;
    // Make this dom visible
    await this.show();
    // Scroll to a current item
    await this.scrollTo( { duration: 0 } );

    // Dissolve patterns based on window size
    if ( isLarge() )
    {
      disableBodyScroll( this.elm, { reserveScrollBarGap: true } );
      await Promise.all( [
        doncyou.open(),
        this.appear(),
      ]);
      await this.console.show();
    }
    else
    {
      await doncyou.open();
      disableBodyScroll( this.elm );
      await this.appear();
      await this.console.show();
    }
    // Listen events
    this.onResize();
    this.$.on( 'scroll', this.scrollID );
    return Promise.resolve();
  }

  async close () {
    resetHeadHash();
    // Clear events
    this.offResize();
    this.$.off( 'scroll', this.scrollID );
    // Hide console
    await this.console.hide();
    // Unlock scroll
    enableBodyScroll( this.elm );
    // Close doncyou
    await Promise.all( [
      this.disappear(),
      doncyou.close(),
    ]);
    await this.hide();
    // Remove cover images
    this.$.empty();
    this.emit( 'close' );
    // Toggle state
    this.opend = ! this.opend;
  }

  show () {
    this.$.css( 'display', 'block' );
    return Promise.resolve();
  }

  hide () {
    this.$.css( 'display', 'none' );
    return Promise.resolve();
  }

  appear () {
    return velocity( this.elm, {
      props: {
        translateX: [ '0%', '100%' ],
      },
      opts: {
        duration: isLarge() ? 600 : 450,
        easing: 'easeOutSine',
      },
    });
  }

  disappear () {
    return velocity( this.elm, {
      props: {
        translateX: [ '100%', '0%' ],
      },
      opts: {
        duration: isLarge() ? 500 : 400,
        easing: 'easeInOutSine',
      },
    });
  }

  scrollTo ( opts = {} ) {
    return velocity( this.col.head, {
      props: 'scroll',
      opts: Object.assign( this.vdefs(), opts ),
    });
  }

  setTouchEvent () {
    let px = null; // Previous x coord
    let sx = null; // Start x coord
    this.elm.addEventListener( 'touchstart', evt => {
      sx = px = evt.touches[ 0 ].clientX;
      // this.showConsole();
    }, false );

    this.elm.addEventListener( 'touchmove', evt => {
      evt.preventDefault();
      const cx = evt.touches[ 0 ].clientX;
      this.elm.scrollLeft -=  ( cx - px );
      px = cx;
    }, false )

    this.elm.addEventListener( 'touchend', evt => {
      const ex = evt.changedTouches[ 0 ].clientX; // End x coord
      const dir = this.getDirection( sx, ex );
      const index = this.col.current + dir;
      if ( this.col.check( index ) )
      {
        this.col.head = index; // Set a current item
        this.scrollTo(); // Scroll to a current item
      }
      else
      {
        console.log( 'Following looks does not exist.' );
      }
    }, false );
  }

  getDirection ( start, end, threshold = 8  ) {
    const diff = Math.abs( end - start );
    if ( diff < threshold )
    {
      return 0;
    }
    else
    {
      return ( start > end ) ? 1 : -1;
    }
  }

  setBtnEvent () {
    this.console.on( 'btn:clicked', ( type ) => {
      if ( type === 'peke' )
      {
        this.close();
        return;
      }
      else if ( type === 'prev' )
      {
        this.col.rewind();
        this.scrollTo( isLarge() ? { duration: 0 } : {} );
        return;
      }
      else if ( type === 'next' )
      {
        this.col.forward();
        this.scrollTo( isLarge() ? { duration: 0 } : {} );
        return;
      }
    });
  }

  resizeLsn () {
    this.scrollTo( { duration: 0 });
  }

  scrollLsn () {
    const width = this.elm.clientWidth;
    const scrollLeft = this.elm.scrollLeft;
    const nume = Math.floor( scrollLeft / width );
    this.console.nume = nume + 1;
  }

  onResize () {
    window.addEventListener( 'resize', this.resizeID );
  }

  offResize () {
    window.removeEventListener( 'resize', this.resizeID );
  }
}
